// src/pages/Dashboard.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { jobService } from '../services/jobService';
import ClientDashboard from '../components/dashboard/ClientDashboard';
import VADashboard from '../components/dashboard/VADashboard';
import { FiUser, FiBriefcase } from 'react-icons/fi';

const Dashboard = () => {
  const { userProfile, updateUserProfile } = useAuth();
  const [userRole, setUserRole] = useState(null);
  const [showRoleSelection, setShowRoleSelection] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (userProfile) {
      checkUserRole();
    }
  }, [userProfile]);

  const checkUserRole = async () => {
    try {
      setLoading(true);
      
      // Check if user has already selected a role
      if (userProfile.role) {
        setUserRole(userProfile.role);
      } else {
        // Check if they have a VA profile
        const vaProfile = await jobService.getVAProfile(userProfile.uid);
        if (vaProfile) {
          setUserRole('va');
          await updateUserProfile({ role: 'va' });
        } else {
          // Show role selection if no role is set
          setShowRoleSelection(true);
        }
      }
    } catch (error) {
      console.error('Error checking user role:', error);
      setShowRoleSelection(true);
    } finally {
      setLoading(false);
    }
  };

  const handleRoleSelection = async (selectedRole) => {
    try {
      await updateUserProfile({ role: selectedRole });
      setUserRole(selectedRole);
      setShowRoleSelection(false);
      
      // If they chose VA, create a basic VA profile
      if (selectedRole === 'va') {
        await jobService.createVAProfile(userProfile.uid, {
          name: userProfile.displayName || userProfile.email?.split('@')[0],
          email: userProfile.email,
          skills: [],
          hourlyRate: 0,
          bio: '',
          availability: 'available'
        });
      }
    } catch (error) {
      console.error('Error setting user role:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (showRoleSelection) {
    return (
      <div className="max-w-4xl mx-auto py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Welcome to ParrotOffice</h1>
          <p className="text-xl text-gray-600">How would you like to use our platform?</p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-2xl mx-auto">
          <div 
            onClick={() => handleRoleSelection('client')}
            className="bg-white p-8 rounded-2xl border-2 border-gray-200 hover:border-blue-500 hover:shadow-lg transition-all cursor-pointer group"
          >
            <div className="text-center">
              <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-blue-200 transition-colors">
                <FiBriefcase className="text-blue-600" size={32} />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">I need help</h3>
              <p className="text-gray-600 mb-6">Post jobs and hire virtual assistants to help with your business tasks</p>
              
              <ul className="text-left space-y-2 text-sm text-gray-600 mb-8">
                <li className="flex items-center">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                  Post job requirements
                </li>
                <li className="flex items-center">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                  Review VA applications
                </li>
                <li className="flex items-center">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                  Manage ongoing projects
                </li>
                <li className="flex items-center">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                  Secure payment system
                </li>
              </ul>
              
              <div className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium group-hover:bg-blue-700 transition-colors">
                Get Started as Client
              </div>
            </div>
          </div>
          
          <div 
            onClick={() => handleRoleSelection('va')}
            className="bg-white p-8 rounded-2xl border-2 border-gray-200 hover:border-green-500 hover:shadow-lg transition-all cursor-pointer group"
          >
            <div className="text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-green-200 transition-colors">
                <FiUser className="text-green-600" size={32} />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">I want to work</h3>
              <p className="text-gray-600 mb-6">Offer your skills and get hired for virtual assistant jobs</p>
              
              <ul className="text-left space-y-2 text-sm text-gray-600 mb-8">
                <li className="flex items-center">
                  <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
                  Create your profile
                </li>
                <li className="flex items-center">
                  <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
                  Browse available jobs
                </li>
                <li className="flex items-center">
                  <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
                  Set your own rates
                </li>
                <li className="flex items-center">
                  <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
                  Build your reputation
                </li>
              </ul>
              
              <div className="px-6 py-3 bg-green-600 text-white rounded-lg font-medium group-hover:bg-green-700 transition-colors">
                Get Started as VA
              </div>
            </div>
          </div>
        </div>
        
        <div className="text-center mt-8 text-gray-500 text-sm">
          You can always switch between roles later in your settings
        </div>
      </div>
    );
  }

  // Render appropriate dashboard based on user role
  if (userRole === 'va') {
    return <VADashboard />;
  } else {
    return <ClientDashboard />;
  }
};

export default Dashboard;